import { useMemo } from 'react';
import { compact } from 'lodash';
import type {
  CommissionInput,
  VcAndOrdersMutation,
  VcAndOrdersMutationHookResult,
  VcAndOrdersMutationVariables,
  VoyageCharter,
  VoyageCharter_OrderFragment,
  VoyageCharter_VoyageCharterFragment,
  VoyageCharterExtraCostInput,
  VoyageCharterExtraFreightInput,
  VoyageChartersQuery,
} from '../../graphql';
import {
  useDeleteVoyageCharterMutation,
  useVoyageCharterQuery,
  VoyageChartersDocument,
} from '../../graphql';
import type { WithId } from '../../lib/util';
import { assertDefined, assertedDefined } from '../../lib/util';
import type { ContractDetailsSchema, VoyageCharterSchema } from './schema';
import { stringToNumOpt } from '../core/util';
import type { ScenarioOrder } from '../../lib/planningData';
import type { Order } from '../core/Core';
import type { AssignableItem } from '../core/suggestions/getSuggestions';
import { hToMs } from '../../lib/time';
import type { VoyageCharterFormSchema } from './VoyageCharterForm';
import type { User } from '../../state/zustand';
import type { OrderWithVoyageCharterValues } from './orders';
import { cacheAddNewOrders } from '../order/order';
import { buildCargoExclusion } from '../order/cargoExclusion';
import useEvent from '../hooks/useEvent';
import type { ApolloClient } from '@apollo/client';
import { mapGroupByMany } from '@seaber/common/lib/collections';
import { StatusFilter } from '../../pages/planning';

export const DEFAULT_LAYCAN_DAYS = 3;

export type NonNullableFields<T> = {
  [P in keyof T]: NonNullable<T[P]>;
};

export async function saveVcAndOrders(
  newVoyageChartersWithOrders: {
    newVoyageCharter: VoyageCharterSchema;
    currentVoyageCharter: VoyageCharter_VoyageCharterFragment | null;
    newOrders: OrderWithVoyageCharterValues[];
    currentOrders: VoyageCharter_OrderFragment[];
  }[],
  synonymMappings: Pick<
    VcAndOrdersMutationVariables,
    'mapLocations' | 'mapAssortments'
  >,
  organisationId: number,
  currentUser: User,
  vcAndOrdersMutation: VcAndOrdersMutationHookResult[0],
  handleAddToAssignment?: (
    targetOrderId: Order['id'] | Array<Order['id']>,
    newAssignmentOrderIds: Array<Order['id']>,
    requestedStartLoad: Date,
  ) => void,
) {
  const vcAndOrders: NonNullableFields<Required<VcAndOrdersMutationVariables>> =
    {
      mapLocations: [],
      mapAssortments: [],
      deleteCargoExclusions: [],
      deleteOrderRequirementAssociations: [],
      deleteOrderAttributeAssociations: [],
      deleteCommissions: [],
      deleteVoyageCharterExtraFreights: [],
      deleteVoyageCharterExtraCosts: [],
      deleteOrderVoyageCharterRelations: [],
      deleteVoyageCharterOrderFreights: [],
      deleteVoyageCharterToContracts: [],
      deleteVoyageCharterPortData: [],
      deleteVoyageCharters: [],
      deletePortData: [],
      createOrders: [],
      createOrderDetails: [],
      createCargoExclusions: [],
      createOrderRequirementAssociations: [],
      createOrderAttributeAssociations: [],
      createPortData: [],
      createVoyageCharters: [],
      createVoyageCharterPortData: [],
      createVoyageCharterToContracts: [],
      createOrderVoyageCharterRelations: [],
      createVoyageCharterOrderFreights: [],
      createVoyageCharterExtraFreights: [],
      createVoyageCharterExtraCosts: [],
      createCommissions: [],
      updateOrders: [],
      updateOrderDetails: [],
      updatePortData: [],
      updateVoyageCharters: [],
      updateVoyageCharterPortData: [],
      updateVoyageCharterToContracts: [],
      updateOrderVoyageCharterRelations: [],
      updateVoyageCharterOrderFreights: [],
      updateVoyageCharterExtraFreights: [],
      updateVoyageCharterExtraCosts: [],
      updateCommissions: [],
    };
  const {
    deleteCommissions,
    deleteVoyageCharterExtraFreights,
    deleteVoyageCharterExtraCosts,
    deleteVoyageCharterPortData,
    deleteOrderVoyageCharterRelations,
    deleteVoyageCharterToContracts,
    deletePortData,
    createOrders,
    createOrderDetails,
    createCargoExclusions,
    createPortData,
    createVoyageCharters,
    createVoyageCharterPortData,
    createVoyageCharterToContracts,
    createOrderVoyageCharterRelations,
    createVoyageCharterOrderFreights,
    createVoyageCharterExtraFreights,
    createVoyageCharterExtraCosts,
    createCommissions,
    updateOrders,
    updateOrderDetails,
    updatePortData,
    updateVoyageCharters,
    updateVoyageCharterToContracts,
    updateOrderVoyageCharterRelations,
    updateVoyageCharterOrderFreights,
    updateVoyageCharterExtraFreights,
    updateVoyageCharterExtraCosts,
    updateCommissions,
  } = vcAndOrders;

  const tmpOrderIdToCreatedIndex = new Map<string, number>();

  const allDeletedOrders: VoyageCharter_OrderFragment[] = [];
  const allOldOrderIds: number[] = [];

  for (const {
    newVoyageCharter,
    currentVoyageCharter,
    newOrders,
    currentOrders,
  } of newVoyageChartersWithOrders) {
    const newOrderIds = new Set(newOrders.map((o) => o.id));

    const createdOrders = newOrders.filter(
      (o): o is typeof o & { id: string } => typeof o.id === 'string',
    );
    const deletedOrders = currentOrders.filter((o) => !newOrderIds.has(o.id));
    allDeletedOrders.push(...deletedOrders);

    const updatedOrders = newOrders.filter(
      (o): o is typeof o & { id: number } => typeof o.id === 'number',
    );

    for (const {
      id,
      extraInfo,
      orderName,
      fixtureNote,
      cargoDeckOption,
      assortmentOptions,
      ...order
    } of createdOrders) {
      const index = createOrders.length;
      tmpOrderIdToCreatedIndex.set(id, index);

      const {
        imoClass,
        specificGravity,
        marginAmount,
        marginUnit,
        cargoExclusions,
        cargoExclusionAmount,
        cargoExclusionType,
      } = assortmentOptions || {};

      createOrders.push({
        ...order,
        specificGravity,
        marginUnit,
        marginAmount,
        organizationId: organisationId,
        softDelete: false,
        loadingOverheadDuration: hToMs(Number(order.loadingOverheadDuration)),
        dischargingOverheadDuration: hToMs(
          Number(order.dischargingOverheadDuration),
        ),
        userId: currentUser?.id,
      });

      createOrderDetails.push({
        orderId: { created: index },
        extraInfo,
        imoClass,
        orderName,
        fixtureNote,
        cargoDeckOption,
      });

      if (
        cargoExclusionType &&
        cargoExclusionAmount &&
        cargoExclusions?.length
      ) {
        const cargoExclusion = cargoExclusions.map((ce) =>
          buildCargoExclusion(
            cargoExclusionType,
            cargoExclusionAmount,
            ce.value,
          ),
        );

        for (const ce of cargoExclusion) {
          createCargoExclusions.push({
            orderId: { created: index },
            ...ce,
          });
        }
      }
    }

    for (const {
      extraInfo,
      orderName,
      fixtureNote,
      cargoDeckOption,
      assortmentOptions,
      id: updatedOrderId,
      loadingOverheadDuration,
      dischargingOverheadDuration,
      ...orderPatch
    } of updatedOrders) {
      const {
        imoClass,
        marginAmount,
        marginUnit,
        specificGravity,
        stowageFactor,
      } = assortmentOptions || {};

      const orderDetailsId =
        currentOrders.find((currentOrder) => currentOrder.id === updatedOrderId)
          ?.orderDetailsByOrderId.nodes[0]?.id ?? null;

      updateOrders.push({
        id: updatedOrderId,
        orderPatch: {
          ...orderPatch,
          marginAmount,
          marginUnit,
          specificGravity,
          stowageFactorMin: stowageFactor?.min,
          stowageFactorMax: stowageFactor?.max,
          loadingOverheadDuration: hToMs(Number(loadingOverheadDuration)),
          dischargingOverheadDuration: hToMs(
            Number(dischargingOverheadDuration),
          ),
        },
      });

      if (orderDetailsId) {
        updateOrderDetails.push({
          id: orderDetailsId,
          orderDetailPatch: {
            extraInfo,
            imoClass,
            orderName,
            fixtureNote,
            cargoDeckOption,
          },
        });
      }
    }

    for (const order of deletedOrders) {
      updateOrders.push({
        id: order.id,
        orderPatch: {
          softDelete: true,
        },
      });
    }

    const oldVoyageCharter = currentVoyageCharter;

    const existingVoyageCharterId = oldVoyageCharter?.id ?? null;
    const voyageCharterData: Pick<
      VoyageCharter,
      'isInquiry' | 'isPreliminary' | 'onSubsUntil' | 'organisationId' | 'name'
    > = {
      isInquiry: newVoyageCharter.contractDetails.type === 'inquiry',
      isPreliminary:
        newVoyageCharter.contractDetails.type === 'coa' &&
        newVoyageCharter.contractDetails.isPreliminary,
      onSubsUntil:
        newVoyageCharter.contractDetails.type === 'spot'
          ? newVoyageCharter.contractDetails.onSubsUntil
          : null,
      organisationId,
      name: null,
    };
    let createdVcId;
    if (existingVoyageCharterId === null) {
      createdVcId = createVoyageCharters.length;
      createVoyageCharters.push({
        ...newVoyageCharter.baseContract,
        ...voyageCharterData,
      });
    } else {
      updateVoyageCharters.push({
        id: existingVoyageCharterId,
        voyageCharterPatch: {
          ...newVoyageCharter.baseContract,
          isInquiry: newVoyageCharter.contractDetails.type === 'inquiry',
          isPreliminary:
            newVoyageCharter.contractDetails.type === 'coa' &&
            newVoyageCharter.contractDetails.isPreliminary,
          onSubsUntil:
            newVoyageCharter.contractDetails.type === 'spot'
              ? newVoyageCharter.contractDetails.onSubsUntil
              : null,
        },
      });
    }

    const voyageCharterId = existingVoyageCharterId
      ? { existing: existingVoyageCharterId }
      : { created: createdVcId };

    const newContract = newVoyageCharter.contractDetails;
    const oldContractRelation =
      oldVoyageCharter?.voyageCharterToContractsByVoyageCharterId.nodes[0] ??
      null;
    if (newContract.type === 'coa' && !oldContractRelation) {
      createVoyageCharterToContracts.push({
        userId: currentUser?.id,
        voyageCharterId,
        coaId: newContract.coaId,
      });
    } else if (
      newContract.type === 'coa' &&
      oldContractRelation &&
      newContract.coaId !== oldContractRelation.coaId
    ) {
      updateVoyageCharterToContracts.push({
        id: oldContractRelation.id,
        voyageCharterToContractPatch: {
          coaId: newContract.coaId,
        },
      });
    } else if (newContract.type !== 'coa' && oldContractRelation) {
      deleteVoyageCharterToContracts.push(oldContractRelation.id);
    }

    const newExtraFreights = newVoyageCharter.extraFreights;
    const oldExtraFreights = compact(
      oldVoyageCharter?.voyageCharterExtraFreightsByVoyageCharterId?.nodes,
    );
    const createdExtraFreights = newExtraFreights.filter(
      (ef) => ef.id === null,
    );

    const deletedExtraFreights = oldExtraFreights.filter(
      (oldEf) => !newExtraFreights.find((newEf) => oldEf.id === newEf.id),
    );

    const updatedExtraFreights = newExtraFreights.filter(
      (newEf): newEf is WithId<Required<VoyageCharterExtraFreightInput>> =>
        !!oldExtraFreights.find(
          (oldEf) =>
            newEf.id === oldEf.id &&
            (newEf.name !== oldEf.name ||
              newEf.amount !== oldEf.amount ||
              newEf.type !== oldEf.type ||
              newEf.description !== oldEf.description ||
              newEf.ignoreInPnl !== oldEf.ignoreInPnl),
        ),
    );

    createdExtraFreights.forEach(
      ({ amount, name, type, description, ignoreInPnl }) =>
        createVoyageCharterExtraFreights.push({
          amount,
          description,
          name,
          type,
          voyageCharterId,
          ignoreInPnl,
        }),
    );

    deletedExtraFreights.forEach((ef) =>
      deleteVoyageCharterExtraFreights.push(ef.id),
    );

    updatedExtraFreights.forEach(
      ({ id, amount, name, type, description, ignoreInPnl }) =>
        updateVoyageCharterExtraFreights.push({
          id,
          voyageCharterExtraFreightPatch: {
            amount,
            description,
            name,
            type,
            ignoreInPnl,
          },
        }),
    );

    const newExtraCosts = newVoyageCharter.extraCosts;
    const oldExtraCosts = compact(
      oldVoyageCharter?.voyageCharterExtraCostsByVoyageCharterId?.nodes,
    );

    const createdExtraCosts = newExtraCosts.filter((ec) => ec.id === null);

    const deletedExtraCosts = oldExtraCosts.filter(
      (oldEc) => !newExtraCosts.find((newEc) => oldEc.id === newEc.id),
    );

    const updatedExtraCosts = newExtraCosts.filter(
      (newEc): newEc is WithId<Required<VoyageCharterExtraCostInput>> =>
        !!oldExtraCosts.find(
          (oldEf) =>
            newEc.id === oldEf.id &&
            (newEc.name !== oldEf.name ||
              newEc.amount !== oldEf.amount ||
              newEc.type !== oldEf.type ||
              newEc.description !== oldEf.description),
        ),
    );

    createdExtraCosts.forEach(({ amount, name, type, description }) =>
      createVoyageCharterExtraCosts.push({
        amount,
        description,
        name,
        type,
        voyageCharterId,
      }),
    );

    deletedExtraCosts.forEach((ec) =>
      deleteVoyageCharterExtraCosts.push(ec.id),
    );

    updatedExtraCosts.forEach(({ id, amount, name, type, description }) =>
      updateVoyageCharterExtraCosts.push({
        id,
        voyageCharterExtraCostPatch: {
          amount,
          description,
          name,
          type,
        },
      }),
    );

    const newCommissions = newVoyageCharter.commissions;
    const oldCommissions = compact(
      currentVoyageCharter?.commissionsByVoyageCharterId.nodes,
    );
    const createdCommissions = newCommissions.filter((cm) => cm.id === null);
    const deletedCommissions = oldCommissions.filter(
      (oldCm) => !newCommissions.find((newCm) => oldCm.id === newCm.id),
    );

    const updatedCommissions = newCommissions.filter(
      (
        newCm,
      ): newCm is WithId<Required<CommissionInput>> & {
        coaRouteId: number | null;
      } =>
        !!oldCommissions.find(
          (oldCm) =>
            newCm.id === oldCm.id &&
            (newCm.type !== oldCm.type ||
              newCm.value !== oldCm.value ||
              newCm.organizationId !== oldCm.organizationId ||
              newCm.demurrageApplied !== oldCm.demurrageApplied ||
              newCm.bafApplied !== oldCm.bafApplied ||
              newCm.extraFreightApplied !== oldCm.extraFreightApplied ||
              newCm.assortmentId !== oldCm.assortmentId ||
              newCm.loadPortId !== oldCm.loadPortId ||
              newCm.dischargePortId !== oldCm.dischargePortId),
        ),
    );

    createdCommissions.forEach(
      ({
        type,
        value,
        bafApplied,
        demurrageApplied,
        extraFreightApplied,
        organizationId,
        assortmentId,
        loadPortId,
        dischargePortId,
      }) =>
        createCommissions.push({
          type,
          value,
          bafApplied,
          demurrageApplied,
          extraFreightApplied,
          organizationId: organizationId ? organizationId : null,
          voyageCharterId,
          assortmentId: assortmentId ? assortmentId : null,
          loadPortId: loadPortId ? loadPortId : null,
          dischargePortId: dischargePortId ? dischargePortId : null,
        }),
    );

    deletedCommissions.forEach((cm) => {
      deleteCommissions.push(cm.id);
    });

    updatedCommissions.forEach(
      ({
        id,
        type,
        value,
        bafApplied,
        demurrageApplied,
        extraFreightApplied,
        organizationId,
        assortmentId,
        loadPortId,
        dischargePortId,
      }) =>
        updateCommissions.push({
          id,
          commissionPatch: {
            type,
            value,
            organizationId: organizationId ? organizationId : null,
            bafApplied,
            demurrageApplied,
            extraFreightApplied,
            assortmentId: assortmentId ? assortmentId : null,
            loadPortId: loadPortId ? loadPortId : null,
            dischargePortId: dischargePortId ? dischargePortId : null,
          },
        }),
    );

    const newRelatedOrders = newVoyageCharter.relatedOrders;
    const oldRelatedOrders = compact(
      oldVoyageCharter?.orderRelations?.nodes,
    ).map((or) => ({
      relationId: or.id,
      orderId: or.orderId,
      coaRouteId: or.coaRouteId ?? null,
      freightId: or.freight?.nodes[0]?.id ?? null,
      freightAmount:
        stringToNumOpt(or.freight?.nodes[0]?.freightAmount) ?? null,
      freightUnit: or.freight?.nodes[0]?.freightUnit ?? null,
    }));
    const oldOrderIds = oldRelatedOrders.map((or) => or.orderId);
    allOldOrderIds.push(...oldOrderIds);

    const createdRelations = newRelatedOrders.filter(
      (o) => typeof o.orderId === 'string' || !oldOrderIds.includes(o.orderId),
    );

    const updatedRelations = newRelatedOrders.filter(
      (newRel): newRel is typeof newRel & { relationId: number } => {
        if (newRel.relationId === null) return false;

        const oldRel =
          oldRelatedOrders.find(
            (oldRel) =>
              newRel.relationId === oldRel.relationId &&
              oldRel.orderId === newRel.orderId,
          ) ?? null;

        return oldRel !== null && newRel.coaRouteId !== oldRel.coaRouteId;
      },
    );

    const deletedRelationIds = oldRelatedOrders
      .filter((oldOrder) => !newOrderIds.has(oldOrder.orderId))
      .map((oldOrder) => oldOrder.relationId);

    const createdRelationIdByOrderId = new Map<number | string, number>();
    createdRelations.map((order) => {
      const orderId = order.orderId;
      createdRelationIdByOrderId.set(
        orderId,
        createOrderVoyageCharterRelations.length,
      );
      createOrderVoyageCharterRelations.push({
        voyageCharterId,
        orderId:
          typeof orderId === 'number'
            ? { existing: orderId }
            : { created: tmpOrderIdToCreatedIndex.get(orderId) },
        coaRouteId: order.coaRouteId,
        outgoing: false,
      });
    });

    for (const updatedOrderRel of updatedRelations) {
      updateOrderVoyageCharterRelations.push({
        id: updatedOrderRel.relationId,
        orderVoyageCharterRelationPatch: {
          coaRouteId: updatedOrderRel.coaRouteId,
        },
      });
    }

    for (const deletedRelId of deletedRelationIds) {
      deleteOrderVoyageCharterRelations.push(deletedRelId);
    }

    const createdFreights = newRelatedOrders.filter((or) => !or.freightId);

    createdFreights.forEach((or) => {
      const relatedOrderId = or.orderId;

      const relationId = or.relationId
        ? { existing: or.relationId }
        : { created: createdRelationIdByOrderId.get(relatedOrderId) };

      createVoyageCharterOrderFreights.push({
        orderVoyageCharterRelationId: relationId,
        freightAmount: or.freightAmount,
        freightUnit: or.freightUnit,
      });
    });

    const updatedFreights = newRelatedOrders.filter((or) => {
      const oldFreight = oldRelatedOrders.find(
        (freight) =>
          freight.freightId !== null && freight.freightId === or.freightId,
      );

      return (
        oldFreight &&
        (or.freightAmount !== oldFreight.freightAmount ||
          or.freightUnit !== oldFreight.freightUnit)
      );
    });

    updatedFreights.forEach((freight) => {
      assertDefined(freight.freightId, 'updated freight id');

      updateVoyageCharterOrderFreights.push({
        id: freight.freightId,
        voyageCharterOrderFreightPatch: {
          freightAmount: freight.freightAmount,
          freightUnit: freight.freightUnit,
        },
      });
    });

    const oldPortDataRelations = compact(
      oldVoyageCharter?.voyageCharterPortDataByVoyageCharterId.nodes,
    );
    const newPortData = newVoyageCharter.portData ?? [];
    const oldPortData = compact(
      oldPortDataRelations.map((pdRel) => pdRel?.portDatumByPortDataId),
    );

    const createdPortData = newPortData.filter((pd) => pd.id === null);
    const deletedPortData = oldPortDataRelations.filter(
      (oldPdRel) =>
        !newPortData.find((newPd) => newPd.id === oldPdRel.portDataId),
    );
    const updatedPortData = newPortData.filter((newPd) => {
      return oldPortData.find((oldPd) => {
        return (
          oldPd.id === newPd.id &&
          (oldPd.loadingDischargeUnit !== newPd.loadingDischargeUnit ||
            oldPd.loadingRate !== newPd.loadingRate ||
            oldPd.dischargeRate !== newPd.dischargeRate ||
            oldPd.portId !== newPd.portId ||
            oldPd.laytimeDays !== newPd.laytimeDays)
        );
      });
    });

    createdPortData.forEach((pd) => {
      const {
        dischargeRate,
        laytimeDays,
        loadingRate,
        loadingDischargeUnit,
        portId,
      } = pd;

      const createdPortDataIndex = createPortData.length;

      createPortData.push({
        dischargeRate: dischargeRate ?? undefined,
        laytimeDays,
        loadingDischargeUnit,
        loadingRate: loadingRate ?? undefined,
        portId,
      });

      createVoyageCharterPortData.push({
        portDataId: { created: createdPortDataIndex },
        voyageCharterId,
      });
    });

    updatedPortData.forEach(
      ({
        id,
        dischargeRate,
        laytimeDays,
        loadingRate,
        loadingDischargeUnit,
        portId,
      }) => {
        if (!id) return;

        updatePortData.push({
          id,
          portDatumPatch: {
            dischargeRate,
            laytimeDays,
            loadingRate,
            loadingDischargeUnit,
            portId,
          },
        });
      },
    );

    deletedPortData.forEach((pdRel) => {
      deletePortData.push(pdRel.portDataId);
      deleteVoyageCharterPortData.push(pdRel.id);
    });
  }

  const result = await vcAndOrdersMutation({
    variables: {
      ...synonymMappings,
      deleteCommissions,
      deleteVoyageCharterExtraFreights,
      deleteVoyageCharterExtraCosts,
      deleteOrderVoyageCharterRelations,
      deleteVoyageCharterToContracts,
      deleteVoyageCharterPortData,
      deletePortData,
      createOrders,
      createOrderDetails,
      createPortData,
      createVoyageCharters,
      createVoyageCharterPortData,
      createVoyageCharterToContracts,
      createOrderVoyageCharterRelations,
      createVoyageCharterOrderFreights,
      createVoyageCharterExtraFreights,
      createVoyageCharterExtraCosts,
      createCommissions,
      updateOrders,
      updateOrderDetails,
      updatePortData,
      updateVoyageCharters,
      updateVoyageCharterToContracts,
      updateOrderVoyageCharterRelations,
      updateVoyageCharterOrderFreights,
      updateVoyageCharterExtraFreights,
      updateVoyageCharterExtraCosts,
      updateCommissions,
    },
    update(cache, { data: { vcAndOrders } }: { data: VcAndOrdersMutation }) {
      if (allDeletedOrders.length) {
        for (const deletedOrder of allDeletedOrders) {
          cache.evict({ id: cache.identify(deletedOrder), broadcast: false });
        }
        cache.gc();
      }

      const createdOrders = vcAndOrders?.createdOrders;
      if (!createdOrders?.length) return;
      // TODO: Calling this function might not be needed anymore?!
      cacheAddNewOrders(createdOrders, cache);
    },
    onCompleted: (data) => {
      setTimeout(() => {
        // Only automatically assigned if some of cargos have been assigned
        if (allOldOrderIds.length > 0 && handleAddToAssignment) {
          const createdOrderIds = compact(
            data?.vcAndOrders?.createdOrders.map((order) => order.id),
          );
          if (createdOrderIds.length) {
            handleAddToAssignment(
              allOldOrderIds,
              [...allOldOrderIds, ...createdOrderIds],
              new Date(1),
            );
          }
        }
      }, 0);
    },
  });

  return {
    voyageCharterIds: [
      ...compact(
        newVoyageChartersWithOrders.map(({ newVoyageCharter: { id } }) => id),
      ),
      ...compact(
        result.data?.vcAndOrders?.createdVoyageCharters.map(({ id }) => id),
      ),
    ],
    orderIds: [
      ...compact(
        newVoyageChartersWithOrders.flatMap(({ newOrders }) =>
          newOrders.map(({ id }) => Number(id)),
        ),
      ),
      ...compact(result.data?.vcAndOrders?.createdOrders.map(({ id }) => id)),
    ],
    createdOrders: compact(result.data?.vcAndOrders?.createdOrders ?? []),
    updatedOrders: compact(result.data?.vcAndOrders?.updatedOrders ?? []),
  };
}

export function useVoyageCharterDelete(
  currentVoyageCharter: VoyageCharter_VoyageCharterFragment | null,
) {
  const [deleteVoyageCharter] = useDeleteVoyageCharterMutation();
  return useEvent(async () => {
    if (!currentVoyageCharter) {
      console.error(
        'Tried to delete voyage charter with no current voyage charter data available',
      );
      return;
    }
    await deleteVoyageCharter({
      variables: {
        input: {
          id: currentVoyageCharter.id,
        },
      },
      update(cache) {
        cache.evict({
          id: cache.identify(currentVoyageCharter),
          broadcast: false,
        });
        cache.gc();
      },
    });
  });
}

function parseVoyageCharterData(
  vc: VoyageCharter_VoyageCharterFragment | null | undefined,
): {
  voyageCharter: VoyageCharter_VoyageCharterFragment | null;
  orders: VoyageCharter_OrderFragment[];
} {
  if (!vc) {
    return {
      orders: [],
      voyageCharter: null,
    };
  }
  // Filter out soft deleted orders
  const filteredOrderRelations = compact(vc.orderRelations.nodes).filter(
    (orderRel) =>
      orderRel.orderByOrderId && !orderRel.orderByOrderId.softDelete,
  );

  const voyageCharter: VoyageCharter_VoyageCharterFragment = {
    ...vc,
    orderRelations: {
      nodes: filteredOrderRelations,
    },
  };

  const orders = compact(
    voyageCharter?.orderRelations.nodes.flatMap((oRel) => oRel?.orderByOrderId),
  );

  return { orders, voyageCharter };
}

export function useVoyageCharterData(voyageCharterId: number | null) {
  const { data, loading, refetch } = useVoyageCharterQuery({
    variables: {
      id: voyageCharterId ?? NaN,
    },
    skip: voyageCharterId === null,
  });

  const voyageCharterById = data?.voyageCharterById;
  const { orders, voyageCharter } = useMemo(
    () => parseVoyageCharterData(voyageCharterById),
    [voyageCharterById],
  );

  return {
    loading,
    orders,
    voyageCharter,
    refetch,
  };
}

export async function fetchVoyageCharterData(
  client: ApolloClient<unknown>,
  voyageCharterIds: number[],
  noCache = true,
) {
  const data = await client.query<VoyageChartersQuery>({
    query: VoyageChartersDocument,
    variables: {
      ids: voyageCharterIds,
    },
    fetchPolicy: noCache ? 'no-cache' : undefined,
  });

  return compact(data.data.allVoyageCharters?.nodes).map(
    parseVoyageCharterData,
  );
}

/** Returns a new array of items for assignment so that every orderId which relates to an incoming voyage charter
 * is replaced by the collection of orders related to this voyage charter. Each order still appears only once in the result. */
export function bundleIncomingVoyageCharterRelatedOrders(
  /** A selection */
  orderIds: number[],
  /** All known orders */
  orderById: Map<number, ScenarioOrder>,
): AssignableItem[] {
  const remainingIndividualOrderIds = new Set(orderIds);
  const selectedOrders = orderIds.map((id) =>
    assertedDefined(
      orderById.get(id),
      `expected selected order ${id} to exist`,
    ),
  );

  const voyageCharters = new Set<number>(
    compact(
      selectedOrders.map(
        (order) => order.incomingVoyageCharterRelation?.voyageCharterId,
      ),
    ),
  );

  const ordersByVoyageCharterId: Map<number, ScenarioOrder[]> = mapGroupByMany(
    [...orderById.values()],
    (o) => {
      const id = o.incomingVoyageCharterRelation?.voyageCharterId;
      return id ? [id] : [];
    },
  );

  for (const order of orderById.values()) {
    const vcId = order.incomingVoyageCharterRelation?.voyageCharterId;
    if (vcId && voyageCharters.has(vcId)) {
      remainingIndividualOrderIds.delete(order.id);
    }
  }

  return [
    ...selectedOrders
      .filter((o) => remainingIndividualOrderIds.has(o.id))
      .map((o) => [o]),
    ...[...voyageCharters].map(
      (voyageCharterId) => ordersByVoyageCharterId.get(voyageCharterId) ?? [],
    ),
  ];
}

/**
 * Returns a unique list of orders indexed be forms order index
 * @param orders
 */
export function getFreights(orders: VoyageCharterFormSchema['relatedOrders']) {
  const freightsByOrderIndex = new Map<
    number,
    VoyageCharterFormSchema['relatedOrders'][number]
  >();

  orders.forEach((o, i) => {
    const loadPort = o.order.loadPortId;
    const dischargePort = o.order.dischargePortId;

    if (loadPort && dischargePort) {
      if (!freightsByOrderIndex.has(i)) {
        freightsByOrderIndex.set(i, o);
      }
    }
  });

  return freightsByOrderIndex;
}

export const getStatusFilterFromContractDetailsType = (
  type: ContractDetailsSchema['type'],
  isPreliminary: boolean,
): StatusFilter => {
  switch (type) {
    case 'coa':
      return isPreliminary ? StatusFilter.COA_PRELIMS : StatusFilter.OPEN;
    case 'inquiry':
      return StatusFilter.INQUIRY;
    case 'spot':
      return StatusFilter.OPEN;
  }
};
